import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TableDropDown } from "@solsys/solsys-reactjs";
import { gql, useQuery } from "@apollo/client";

const gqlQuery = gql`
  query ticketDropDown($ticket_list_request: ticket_list_request) {
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        name
        fsr_id
      }
      totalCount
    }
  }
`;

const mapTicketRow = (item) => ({
  ...item,
  ID: item.id,
  Name: item.name,
});

const TicketDropDown = ({ selected, ...props }) => {
  selected = selected === -1 ? undefined : selected;

  const truncateLength = 25;
  const craneFilterKey = String(props.craneId ?? "");

  const [requestState, setRequestState] = useState({
    page: 1,
    pageSize: 20,
    search: "",
  });
  const { page, pageSize, search } = requestState;

  const [accumulatedRaw, setAccumulatedRaw] = useState([]);
  const filtersKey = JSON.stringify(props.filters ?? []);
  const sortingKey = JSON.stringify(props.sorting ?? []);

  let variables = {
    ticket_list_request: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: [],
      sorting: [
        {
          direction: "DESC",
          field: "id",
        },
      ],
    },
  };

  let renderVariables = {
    ticket_list_request: {
      pagination: {
        limit: truncateLength,
        offset: 0,
      },
      filters: [],
      sorting: [
        {
          direction: "DESC",
          field: "id",
        },
      ],
    },
  };

  if (
    (Array.isArray(selected) && selected.length > 0) ||
    Number.isInteger(selected)
  ) {
    let array = [];
    if (props.multiple) {
      selected.forEach((v) => array.push(v));
    } else {
      array.push(selected);
    }
    renderVariables.ticket_list_request.filters.push({
      field: "id",
      operator: "in",
      value: `(${array.join(", ")})`,
    });
  }

  if (props.craneId) {
    variables.ticket_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(props.craneId),
    });
  }

  if (search && search !== "")
    variables.ticket_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${search}%`,
    });
  if (props.filters?.length > 0) {
    props.filters.forEach((f) =>
      variables.ticket_list_request.filters.push(f)
    );
  }
  if (props.sorting?.length > 0) {
    props.sorting.forEach(
      (s) => (variables.ticket_list_request.sorting = [s])
    );
  }

  let { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
    variables: renderVariables,
  });
  const renderLoading = renderObject.loading;
  const renderError = renderObject.error;
  const renderData = renderObject.data;

  useEffect(() => {
    renderRefetch();
  }, [selected, renderRefetch]);

  const renderEntityListResponse = renderData?.ticket;
  const renderEntityRaw = renderEntityListResponse?.edges ?? [];
  const renderEntity = renderEntityRaw.map(mapTicketRow);

  const { refetch: refetchTicket, loading, error, data } = useQuery(gqlQuery, {
    variables,
  });

  useEffect(() => {
    if (!props.disabled) refetchTicket();
  }, [page, search, filtersKey, sortingKey, craneFilterKey, refetchTicket, props.disabled]);

  useEffect(() => {
    setAccumulatedRaw([]);
    setRequestState((s) => ({ ...s, page: 1 }));
  }, [search, filtersKey, sortingKey, craneFilterKey]);

  useEffect(() => {
    if (loading) return;
    const edges = data?.ticket?.edges;
    if (!edges) return;
    setAccumulatedRaw((prev) => {
      if (page === 1) return [...edges];
      const seen = new Set(prev.map((x) => Number(x.id)));
      const toAdd = edges.filter((x) => !seen.has(Number(x.id)));
      return [...prev, ...toAdd];
    });
  }, [data, page, loading]);

  const excludeSet = useMemo(
    () => new Set((props.excludeIds ?? []).map((id) => Number(id))),
    [props.excludeIds]
  );

  const totalCountRef = useRef(0);
  totalCountRef.current = data?.ticket?.totalCount ?? 0;
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedRaw.length;

  const onSearch = (event) => {
    let { value } = event.target;
    setRequestState((s) => ({
      ...s,
      search: value,
      page: 1,
    }));
  };

  const paginationChange = (newPage) => {
    setRequestState((s) => ({
      ...s,
      page: newPage,
    }));
  };

  const addMoreResultsHandler = useCallback(() => {
    if (props.disabled) return;
    setRequestState((s) => {
      if (accumulatedLenRef.current >= totalCountRef.current) return s;
      return { ...s, page: s.page + 1 };
    });
  }, [props.disabled]);

  const entityList = accumulatedRaw
    .filter((item) => !excludeSet.has(Number(item.id)))
    .filter((item) => (props.onlyWithoutFsr ? item.fsr_id == null : true))
    .map(mapTicketRow);

  const entityCount = data?.ticket?.totalCount ?? 0;

  if (error) console.error(`Ticket drop down data error : ${error.message}`);
  if (renderError)
    console.error(`Ticket drop down render error : ${renderError.message}`);

  const headers = [
    { label: "ID" },
    { label: "Name" },
  ];

  return (
    <TableDropDown
      {...props}
      items={entityList}
      loading={loading}
      renderLoading={renderLoading}
      multiple={props.multiple}
      searchEnabled={props.searchEnabled}
      searchValue={search}
      onSearch={onSearch}
      selectValue={selected}
      selectRender={renderEntity}
      onSelect={props.onSelect}
      dataKey={"id"}
      identityColumnKey={"ID"}
      renderColumnKey={"Name"}
      headers={headers}
      truncateLength={25}
      entityCount={entityCount}
      onPaginationChange={paginationChange}
      addMoreResults={
        typeof props.addMoreResults === "function"
          ? props.addMoreResults
          : addMoreResultsHandler
      }
    />
  );
};

export default TicketDropDown;
